import { LocalDate } from "@js-joda/core";
import { SalaryType } from "model/Salary";
import type { CreateWorkerEmploymentRequest } from "services/api/company/staff.service";
import type {
  SaveEmploymentResult,
  SaveEmploymentParams,
} from "../../profilePanel.contract";
import {
  parseIsoDateToLocalDateOrNull,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";

/**
 * Parses ISO date or date-time text into LocalDate.
 * Returns null when value is missing or invalid.
 */
export const toLocalDateOrNull = (
  value: string | null | undefined,
): LocalDate | null => {
  return parseIsoDateToLocalDateOrNull(value);
};

/**
 * Converts Stockholm start-of-day for LocalDate to a UTC ISO instant.
 */
export const toIsoDateStartOfDayUtc = (date: LocalDate): string => {
  return toIsoStartOfDayUtcFromStockholmDate(date);
};

const toIsoDateStartOfDayUtcOrNull = (value: string | null): string | null => {
  const parsedDate = toLocalDateOrNull(value);
  if (parsedDate == null) return null;
  return toIsoDateStartOfDayUtc(parsedDate);
};

/**
 * Parses a numeric string and clamps negative/invalid values to 0.
 */
export const toNonNegativeNumber = (value: string): number => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.max(0, parsedValue);
};

/**
 * Parses an integer string and clamps invalid/negative values to 0.
 */
export const toNonNegativeIntegerOrZero = (value: string): number => {
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
};

const toEmploymentBasePayload = (
  params: SaveEmploymentParams,
): Omit<CreateWorkerEmploymentRequest, "salaryValue"> => {
  const workPercentagePercent = toNonNegativeNumber(
    params.editedEmploymentDetails.workPercentage,
  );
  const workPercentage =
    params.editedEmploymentDetails.salaryType === SalaryType.Monthly
      ? Math.min(workPercentagePercent / 100, 1)
      : 0;

  return {
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    endDate: params.effectiveEndDate
      ? toIsoDateStartOfDayUtc(params.effectiveEndDate)
      : null,
    employmentType:
      params.editedEmploymentDetails.employmentType || "Undefined",
    employeeNumber: toNonNegativeIntegerOrZero(
      params.editedEmploymentDetails.employeeNumber,
    ),
    employeeCategory:
      params.editedEmploymentDetails.employeeCategory || "Undefined",
    salaryType: params.editedEmploymentDetails.salaryType || "Undefined",
    workPercentage,
    paymentType: params.editedEmploymentDetails.paymentType || "None",
    role: params.editedEmploymentDetails.role,
    agreement: params.editedEmploymentDetails.agreement,
    vacationDays: toNonNegativeIntegerOrZero(
      params.editedEmploymentDetails.vacationDays,
    ),
    noticeDays: toNonNegativeIntegerOrZero(
      params.editedEmploymentDetails.noticeDays,
    ),
    workplace: params.editedEmploymentDetails.workplace,
    probationPeriodEndDate: toIsoDateStartOfDayUtcOrNull(
      params.probationPeriodEndDate,
    ),
  };
};

/**
 * Builds API payload for updating current worker employment data.
 */
export const buildUpdateStaffWorkerPayload = (
  params: SaveEmploymentParams,
): SaveEmploymentResult => {
  return {
    payload: toEmploymentBasePayload(params),
  };
};

/**
 * Builds API payload for creating a new worker employment.
 */
export const buildCreateWorkerEmploymentPayload = (
  params: SaveEmploymentParams,
): { payload: CreateWorkerEmploymentRequest } => {
  return {
    payload: {
      ...toEmploymentBasePayload(params),
      salaryValue: toNonNegativeNumber(params.editedEmploymentDetails.salary),
    },
  };
};
